import React, { useRef, useEffect, useLayoutEffect, useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import * as d3 from 'd3';
import { Slider, Box, Button } from '@mui/material';
import { RestartAlt } from '@mui/icons-material';

// Plot margins (must match the values used when drawing the SVG below) so the
// zoom sliders can be aligned to the plot area.
const CHART_MARGIN = { top: 30, right: 30, bottom: 70, left: 90 };
// Width of the column reserved for the vertical (Y-axis) zoom slider.
const Y_SLIDER_COL = 48;

// Standard-deviation (in plot pixels) of the Gaussian kernel used by
// d3.contourDensity when deriving the density cloud from the raw data points.
// Larger = smoother/broader cloud, smaller = tighter and more detailed.
const DENSITY_BANDWIDTH = 20;

// Number of contour bands d3.contourDensity generates. More bands = finer color
// and opacity steps, so the gradient between levels reads as a smooth
// transition rather than a few visible striations (which the edge fade would
// otherwise expose, especially in sparse regions).
const DENSITY_THRESHOLD_COUNT = 60;

// Fraction of the domain span added as breathing room on every outer edge so
// data points and cutoff lines are never drawn flush against (or clipped by)
// the chart boundary. Applied symmetrically; because these values are always
// non-negative, the lower bound simply dips a little below zero.
const AXIS_PADDING_FRACTION = 0.05;

// Expand a [min, max] domain outward on both ends by AXIS_PADDING_FRACTION of
// its span (with a small absolute fallback for a zero/degenerate span).
const padDomain = ([min, max]) => {
  const span = max - min;
  const pad = (span > 0 ? span : Math.abs(max) || 1) * AXIS_PADDING_FRACTION;
  return [min - pad, max + pad];
};

// Clamp a proposed [min, max] domain within the given outer bounds while
// enforcing a minimum span so the user cannot zoom in past a readable range.
const clampDomain = (domain, bounds) => {
  const outerSpan = bounds[1] - bounds[0];
  const minSpan = outerSpan > 0 ? outerSpan * 0.01 : 1e-6;
  let [a, b] = domain;
  if (b - a < minSpan) {
    const mid = (a + b) / 2;
    a = mid - minSpan / 2;
    b = mid + minSpan / 2;
  }
  const span = b - a;
  if (span >= outerSpan) return [bounds[0], bounds[1]];
  if (a < bounds[0]) { a = bounds[0]; b = a + span; }
  if (b > bounds[1]) { b = bounds[1]; a = b - span; }
  return [a, b];
};

const ScatterplotChart = forwardRef(({ data, height = 500, subtitle = '', showCutoffLines = false, showRegressionLine = false, showDensityCloud = false, displayTitle = '' }, ref) => {
  const svgRef = useRef();
  const tooltipRef = useRef();
  const clipIdRef = useRef('scatter-clip-' + Math.random().toString(36).slice(2));
  const ySliderRef = useRef(null);

  const [width, setWidth] = useState(800);
const containerRef = useRef();

useEffect(() => {
  if (!containerRef.current) return;
  const handleResize = () => {
    if (containerRef.current) {
      // Reserve space for the vertical zoom slider column so the SVG fits the
      // remaining width without overflowing the container.
      setWidth(Math.max(containerRef.current.offsetWidth - Y_SLIDER_COL, 200) || 800);
    }
  };
  handleResize();
  const resizeObserver = new window.ResizeObserver(handleResize);
  resizeObserver.observe(containerRef.current);
  return () => resizeObserver.disconnect();
}, []);

  // The vertical Y-zoom Slider is a range (two thumbs): MUI renders the minimum
  // thumb (bottom) before the maximum thumb (top) in the DOM, so keyboard Tab
  // reaches the bottom handle first — the opposite of natural top-to-bottom
  // flow. Reverse the two thumbs' DOM order so Tab lands on the top (maximum)
  // handle first. Done via DOM order (not tabIndex) to avoid pulling the thumbs
  // to the front of the modal's focus cycle. The horizontal X slider is left
  // as-is since its left-to-right flow is already natural.
  useLayoutEffect(() => {
    const root = ySliderRef.current;
    if (!root) return;
    const minThumb = root.querySelector('.MuiSlider-thumb[data-index="0"]');
    const maxThumb = root.querySelector('.MuiSlider-thumb[data-index="1"]');
    if (minThumb && maxThumb && maxThumb.parentNode && minThumb.previousElementSibling !== maxThumb) {
      minThumb.parentNode.insertBefore(maxThumb, minThumb);
    }
  });

  // Extract dataPoints and axis configs from API response
  const dataPoints = data?.dataPoints || [];
  const xAxisTitle = data?.xAxisConfig?.axisTitle || '';
  const xAxisUnits = data?.xAxisConfig?.axisUnits || '';
  const xAxisCutoffValues = data?.xAxisConfig?.cutoffValues || {};
  const yAxisTitle = data?.yAxisConfig?.axisTitle || '';
  const yAxisUnits = data?.yAxisConfig?.axisUnits || '';
  const yAxisCutoffValues = data?.yAxisConfig?.cutoffValues || {};
  const regressionOutput = data?.regressionOutput || null;

  // Whether the chart has meaningful data to render (used for zoom/slider gating).
  const EPSILON = 1e-10;
  const allXZero = dataPoints.length > 0 && dataPoints.every(point => Math.abs(point.x) < EPSILON);
  const allYZero = dataPoints.length > 0 && dataPoints.every(point => Math.abs(point.y) < EPSILON);
  const showNoData = dataPoints.length === 0 || allXZero || allYZero;

  // --- Zoom state & domains -------------------------------------------------
  // Raw data extent (unpadded), anchored at 0 on both axes.
  const dataExtent = useMemo(() => {
    const pts = data?.dataPoints || [];
    let xMax = d3.max(pts, d => d.x) ?? 1;
    let yMax = d3.max(pts, d => d.y) ?? 1;
    if (xMax < 0.2) xMax = 0.2;
    if (yMax < 0.2) yMax = 0.2;
    return { x: [0, xMax], y: [0, yMax] };
  }, [data]);

  // Base domain fit to the data only, padded so edge points aren't clipped.
  const dataDomain = useMemo(() => ({
    x: padDomain(dataExtent.x),
    y: padDomain(dataExtent.y),
  }), [dataExtent]);

  // Density cloud contours, derived on the UI from the raw data points via
  // d3.contourDensity (the endpoint's densityContours array is intentionally
  // ignored). The KDE is run in the PIXEL space of the default (unzoomed) view
  // so the kernel is visually isotropic, then each contour ring is mapped back
  // to DATA space via the same scales' invert(). Keeping the stored contours in
  // data space lets the draw effect re-project them through the live x/y scales
  // on every zoom/pan with no recompute — this memo only re-runs when the data,
  // the density toggle, or the plot size changes (never on zoom).
  const densityContours = useMemo(() => {
    if (!showDensityCloud || dataPoints.length < 2) return [];
    const plotW = Math.max(width - CHART_MARGIN.left - CHART_MARGIN.right, 1);
    const plotH = Math.max(height - CHART_MARGIN.top - CHART_MARGIN.bottom, 1);
    const px = d3.scaleLinear().domain(dataDomain.x).range([0, plotW]);
    const py = d3.scaleLinear().domain(dataDomain.y).range([plotH, 0]);
    const contours = d3.contourDensity()
      .x(d => px(d.x))
      .y(d => py(d.y))
      .size([plotW, plotH])
      .bandwidth(DENSITY_BANDWIDTH)
      .thresholds(DENSITY_THRESHOLD_COUNT)(dataPoints);
    return contours.map(c => ({
      value: c.value,
      coordinates: c.coordinates.map(polygon =>
        polygon.map(ring => ring.map(([mx, my]) => [px.invert(mx), py.invert(my)]))
      ),
    }));
  }, [dataPoints, showDensityCloud, dataDomain, width, height]);

  // Domain expanded to include any configured cutoff values. Used as the outer
  // bound for the zoom sliders/wheel and as the default view when cutoff lines
  // are displayed.
  const fullDomain = useMemo(() => {
    const xc = data?.xAxisConfig?.cutoffValues || {};
    const yc = data?.yAxisConfig?.cutoffValues || {};
    const num = (v) => (v === undefined || v === null || v === '' ? null : Number(v));
    const finite = (arr) => arr.filter((v) => v !== null && !Number.isNaN(v));

    // A cutoff floor/ceiling can lie on either side of the data range (e.g. a
    // normal-range floor whose value is greater than every data point), so each
    // cutoff value must be considered for both the lower and upper bound.
    const xCuts = finite([num(xc.normalFloor), num(xc.normalCeiling)]);
    const yCuts = finite([num(yc.normalFloor), num(yc.normalCeiling)]);

    let xMin = 0;
    let yMin = 0;
    let xMax = dataExtent.x[1];
    let yMax = dataExtent.y[1];
    xCuts.forEach((v) => { xMin = Math.min(xMin, v); xMax = Math.max(xMax, v); });
    yCuts.forEach((v) => { yMin = Math.min(yMin, v); yMax = Math.max(yMax, v); });

    // Pad the combined extent so cutoff lines aren't flush against the edge.
    return { x: padDomain([xMin, xMax]), y: padDomain([yMin, yMax]) };
  }, [data, dataExtent]);

  // The default (fitted) view: includes cutoffs only when they are displayed.
  const defaultDomain = showCutoffLines ? fullDomain : dataDomain;

  // User-driven zoom overrides. `null` means "auto-fit to the default view".
  const [userXDomain, setUserXDomain] = useState(null);
  const [userYDomain, setUserYDomain] = useState(null);

  // The domain actually rendered: the user's manual zoom when present, otherwise
  // the default fitted view. Deriving this during render (instead of via an
  // effect) guarantees the chart zooms out to include cutoffs the moment the
  // cutoff-lines toggle flips, with no one-render lag.
  const xDomain = userXDomain || defaultDomain.x;
  const yDomain = userYDomain || defaultDomain.y;

  // Clear any manual zoom when the data changes or the cutoff-lines toggle
  // flips, so the chart re-fits to the (possibly cutoff-inclusive) default view.
  useEffect(() => {
    setUserXDomain(null);
    setUserYDomain(null);
  }, [data, showCutoffLines]);

  // Latest zoom values for the (native, non-passive) wheel handler.
  // Outer bounds follow the default view: data-only when cutoff lines are off,
  // cutoff-inclusive when they are on. This keeps the axes fit to the data by
  // default instead of appearing scaled out to a far-away cutoff.
  const zoomStateRef = useRef({});
  zoomStateRef.current = {
    xDomain,
    yDomain,
    outerX: defaultDomain.x,
    outerY: defaultDomain.y,
    plotWidth: Math.max(width - CHART_MARGIN.left - CHART_MARGIN.right, 1),
    plotHeight: Math.max(height - CHART_MARGIN.top - CHART_MARGIN.bottom, 1),
  };

  // Mouse-wheel zoom (both axes, anchored at the cursor). Kept in sync with the
  // sliders because they share the same xDomain/yDomain state.
  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return undefined;
    const onWheel = (event) => {
      const st = zoomStateRef.current;
      if (!st.xDomain || !st.yDomain) return;
      event.preventDefault();
      const rect = svgEl.getBoundingClientRect();
      let px = event.clientX - rect.left - CHART_MARGIN.left;
      let py = event.clientY - rect.top - CHART_MARGIN.top;
      px = Math.max(0, Math.min(st.plotWidth, px));
      py = Math.max(0, Math.min(st.plotHeight, py));
      const factor = event.deltaY > 0 ? 1.15 : 1 / 1.15;
      const xData = st.xDomain[0] + (px / st.plotWidth) * (st.xDomain[1] - st.xDomain[0]);
      const yData = st.yDomain[1] - (py / st.plotHeight) * (st.yDomain[1] - st.yDomain[0]);
      setUserXDomain(clampDomain(
        [xData - (xData - st.xDomain[0]) * factor, xData + (st.xDomain[1] - xData) * factor],
        st.outerX
      ));
      setUserYDomain(clampDomain(
        [yData - (yData - st.yDomain[0]) * factor, yData + (st.yDomain[1] - yData) * factor],
        st.outerY
      ));
    };
    svgEl.addEventListener('wheel', onWheel, { passive: false });
    return () => svgEl.removeEventListener('wheel', onWheel);
  }, [showNoData]);

  // Click-and-drag to pan the (zoomed) plot. Shares the same xDomain/yDomain
  // state as the wheel and sliders, so panning slides the slider handles too.
  // clampDomain preserves the current span and keeps the view inside the outer
  // bounds, so a drag repositions without changing the zoom level.
  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return undefined;
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    let pendingDX = 0;
    let pendingDY = 0;
    let rafId = null;

    // Apply the accumulated pixel movement once per animation frame. Coalescing
    // many mousemove events into a single state update (and therefore a single
    // React re-render + D3 redraw) keeps panning smooth on slower machines.
    const flush = () => {
      rafId = null;
      if (!pendingDX && !pendingDY) return;
      const st = zoomStateRef.current;
      if (!st.xDomain || !st.yDomain) {
        pendingDX = 0;
        pendingDY = 0;
        return;
      }
      const xSpan = st.xDomain[1] - st.xDomain[0];
      const ySpan = st.yDomain[1] - st.yDomain[0];
      const dxData = (pendingDX * xSpan) / st.plotWidth;
      const dyData = (pendingDY * ySpan) / st.plotHeight;
      pendingDX = 0;
      pendingDY = 0;
      // Drag right -> reveal smaller x (subtract); drag down -> reveal larger y
      // (add, because the y range is inverted). Keeps the grabbed point anchored.
      const nextX = clampDomain([st.xDomain[0] - dxData, st.xDomain[1] - dxData], st.outerX);
      const nextY = clampDomain([st.yDomain[0] + dyData, st.yDomain[1] + dyData], st.outerY);
      // Skip the state update (and redraw) when clamping pins us at a boundary
      // so the domain hasn't actually changed.
      if (nextX[0] !== st.xDomain[0] || nextX[1] !== st.xDomain[1]) setUserXDomain(nextX);
      if (nextY[0] !== st.yDomain[0] || nextY[1] !== st.yDomain[1]) setUserYDomain(nextY);
    };

    const onDown = (event) => {
      // Data points have hover tooltips but no click action, so it's safe to
      // start a pan on top of one — the tooltip still shows while hovering.
      dragging = true;
      lastX = event.clientX;
      lastY = event.clientY;
      svgEl.style.cursor = 'grabbing';
      event.preventDefault(); // avoid selecting axis/label text while dragging
    };
    const onMove = (event) => {
      if (!dragging) return;
      pendingDX += event.clientX - lastX;
      pendingDY += event.clientY - lastY;
      lastX = event.clientX;
      lastY = event.clientY;
      if (rafId === null) rafId = requestAnimationFrame(flush);
    };
    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      svgEl.style.cursor = 'grab';
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      flush(); // commit any movement left over from the final frame
    };

    svgEl.style.cursor = 'grab';
    svgEl.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', endDrag);
    return () => {
      svgEl.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', endDrag);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [showNoData]);

  const handleXSliderChange = (_event, value) => setUserXDomain(clampDomain(value, defaultDomain.x));
  const handleYSliderChange = (_event, value) => setUserYDomain(clampDomain(value, defaultDomain.y));
  const handleResetZoom = () => {
    setUserXDomain(null);
    setUserYDomain(null);
  };
  const xSliderStep = Math.max((defaultDomain.x[1] - defaultDomain.x[0]) / 200, 0.0001);
  const ySliderStep = Math.max((defaultDomain.y[1] - defaultDomain.y[0]) / 200, 0.0001);

    const styles = getComputedStyle(document.documentElement);
    const primaryColor = styles.getPropertyValue('--primary-color').trim();
    const secondaryColor = styles.getPropertyValue('--secondary-color').trim();
    const tertiaryColor = styles.getPropertyValue('--tertiary-color').trim();
    const accentPrimaryColor = styles.getPropertyValue('--accent-primary-color').trim();
    const accentSecondaryColor = styles.getPropertyValue('--accent-secondary-color').trim();
    const accentTertiaryColor = styles.getPropertyValue('--accent-tertiary-color').trim();
    const infoColor = styles.getPropertyValue('--info-color').trim();

  // Export as PNG
  useImperativeHandle(ref, () => ({
    exportAsPNG: () => {
      const svgNode = svgRef.current;
      if (!svgNode) return;
      const serializer = new XMLSerializer();
      let svgString = serializer.serializeToString(svgNode);

      // Replace dot color in SVG string before rendering to PNG
      const styleTag = `<style>
        text { font-family: "Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif; }
      </style>`;
      svgString = svgString.replace(/<svg([^>]*)>/, `<svg$1>${styleTag}`);
      svgString = svgString
      .replace(/var\(--primary-color\)/g, primaryColor || '#006778')
      .replace(/var\(--accent-primary-color\)/g, accentPrimaryColor || '#bb4d00')
      .replace(/var\(--accent-tertiary-color\)/g, accentTertiaryColor || '#ffe97d')
      .replace(/var\(--info-color\)/g, infoColor || '#4173B4');

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Get chart title and subtitle from data, unless overridden by subtitle prop (sent from PatientDetails)
      // Use displayTitle if provided, otherwise fall back to data.plotProperties.plotTitle
      const plotTitle = displayTitle || data?.plotProperties?.plotTitle || '';
      const plotSubtitle = subtitle || data?.plotProperties?.plotSubtitle || '';

      // Use app font
      const appFont = '"Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif';
      ctx.font = `20px ${appFont}`;

      // Word-wrap logic for title
      const maxLineWidth = svgNode.width.baseVal.value * 0.9;
      const titleLines = [];
      let currentLine = plotTitle.split(' ')[0];
      const words = plotTitle.split(' ');
      for (let i = 1; i < words.length; i++) {
        const testLine = currentLine + ' ' + words[i];
        if (ctx.measureText(testLine).width < maxLineWidth) {
          currentLine = testLine;
        } else {
          titleLines.push(currentLine);
          currentLine = words[i];
        }
      }
      titleLines.push(currentLine);

      // Subtitle
      if (plotSubtitle) titleLines.push(plotSubtitle);

      const lineHeight = 28;
      const titleHeight = titleLines.length * lineHeight + 10;

      // Set canvas size
      canvas.width = svgNode.width.baseVal.value;
      canvas.height = svgNode.height.baseVal.value + titleHeight;

      // Set font and styles again after setting canvas size
      ctx.font = `20px ${appFont}`;
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'center';

      // Fill background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw title lines
      ctx.fillStyle = '#000000';
      titleLines.forEach((line, i) => {
        ctx.fillText(line, canvas.width / 2, 32 + i * lineHeight);
      });

      // Draw chart below title
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();

      img.onload = () => {
        ctx.drawImage(img, 0, titleHeight);

        URL.revokeObjectURL(url);

        const pngUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = pngUrl;
        link.download = `ED3N-Scatterplot-Chart-${plotTitle.replace(/\s+/g, '_')}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

      img.src = url;
    }
  }));

  useEffect(() => {
    // Early return if no data to prevent accessing null svgRef
    if (!dataPoints || dataPoints.length === 0) return;
    
    // Early return if all x or y values are zero or near-zero (not a meaningful scatterplot)
    const EPSILON = 1e-10;
    const allXZero = dataPoints.every(point => Math.abs(point.x) < EPSILON);
    const allYZero = dataPoints.every(point => Math.abs(point.y) < EPSILON);
    if (allXZero || allYZero) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const margin = { top: 30, right: 30, bottom: 70, left: 90 };
    const plotWidth = width - margin.left - margin.right;
    const plotHeight = height - margin.top - margin.bottom;

    // Visible domain is driven by the zoom state (sliders + mouse wheel).
    const [xMin, xMax] = xDomain;
    const [yMin, yMax] = yDomain;

    const x = d3.scaleLinear()
      .domain([xMin, xMax])
      .range([0, plotWidth]);
    const y = d3.scaleLinear()
      .domain([yMin, yMax])
      .range([plotHeight, 0]);

      const g = svg.append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

    // Clip path so zoomed-in marks stay within the plot area.
    const clipId = clipIdRef.current;
    const defs = svg.append('defs');
    defs
      .append('clipPath')
      .attr('id', clipId)
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', plotWidth)
      .attr('height', plotHeight);

    // Gaussian blur filter for the density cloud. The raw contours are many
    // thin nested rings (and small islands), which read as "spaghetti" when
    // filled individually. A light blur merges them just enough to look like a
    // continuous cloud while keeping visible definition between the bands (a
    // larger radius smears everything out and also dilutes the color).
    const densityBlurId = `${clipId}-density-blur`;
    defs
      .append('filter')
      .attr('id', densityBlurId)
      .attr('x', '-20%')
      .attr('y', '-20%')
      .attr('width', '140%')
      .attr('height', '140%')
      .append('feGaussianBlur')
      .attr('in', 'SourceGraphic')
      .attr('stdDeviation', 4);
    const plotArea = g.append('g')
      .attr('clip-path', `url(#${clipId})`);

    // Horizontal grid lines
    g.append('g')
      .attr('class', 'grid')
      .attr('opacity', 0.1)
      .call(d3.axisLeft(y)
        .tickSize(-plotWidth)
        .tickFormat(''));

    // Vertical grid lines
    g.append('g')
      .attr('class', 'grid')
      .attr('opacity', 0.1)
      .attr('transform', `translate(0,${plotHeight})`)
      .call(d3.axisBottom(x)
        .tickSize(-plotHeight)
        .tickFormat(''));

    // X axis
    g.append('g')
      .attr('transform', `translate(0,${plotHeight})`)
      .call(d3.axisBottom(x))
      .selectAll('.tick text')
      .attr('font-size', '16px')
      .attr('font-family', '"Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif'); 

    // X label (title + units) — sits near the very bottom of the SVG
    svg.append('text')
      .attr('x', margin.left + plotWidth / 2)
      .attr('y', height - 8)
      .attr('text-anchor', 'middle')
      .attr('font-size', '16px')
      .attr('font-family', '"Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif') 
      .text(`${xAxisTitle}${xAxisUnits ? ' (' + xAxisUnits + ')' : ''}`);

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y))
      .selectAll('.tick text')
      .attr('font-size', '16px') 
      .attr('font-family', '"Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif'); 

    // Y label (title + units) - break into two lines if needed
    function splitLabelByPixelWidth(text, font, maxWidth) {
      const words = text.split(' ');
      const lines = [];
      let line = words[0];
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.font = font;
      for (let i = 1; i < words.length; i++) {
        const testLine = line + ' ' + words[i];
        if (ctx.measureText(testLine).width < maxWidth) {
          line = testLine;
        } else {
          lines.push(line);
          line = words[i];
        }
      }
      lines.push(line);
      // If a single word is longer than maxWidth, break it with a '-'
      for (let i = 0; i < lines.length; i++) {
        if (ctx.measureText(lines[i]).width > maxWidth) {
          const str = lines[i];
          lines[i] = str.slice(0, Math.floor(str.length / 2)) + '-';
          lines.splice(i + 1, 0, str.slice(Math.floor(str.length / 2)));
        }
      }
      return lines;
    }

    const fontSize = 16;
    const appFont = '"Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif"';
    const font = `${fontSize}px ${appFont}`;
    const maxPixelWidth = 300; 

    const yLabelText = `${yAxisTitle}${yAxisUnits ? ' (' + yAxisUnits + ')' : ''}`;
    const yLabelLines = splitLabelByPixelWidth(yLabelText, font, maxPixelWidth);

    const totalLabelHeight = yLabelLines.length * 20; // 20px line spacing
    const yLabelY = margin.left / 2 - totalLabelHeight / 2 - 10;

    const yLabel = svg.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -height / 2)
      .attr('y', yLabelY)
      .attr('text-anchor', 'middle')
      .attr('font-size', fontSize)
      .attr('font-family', appFont);

    yLabel.selectAll('tspan')
      .data(yLabelLines)
      .enter()
      .append('tspan')
      .attr('x', -height / 2)
      .attr('dy', (_, i) => i === 0 ? 0 : 20)
      .text(d => d);

    // Create tooltip div
    const container = d3.select(svgRef.current.parentElement);
    let tooltip = container.select('.scatterplot-tooltip');
    if (tooltip.empty()) {
      tooltip = container.append('div')
        .attr('class', 'scatterplot-tooltip')
        .style('position', 'absolute')
        .style('background', '#fff')
        .style('border', '1px solid #333')
        .style('border-radius', '4px')
        .style('padding', '6px 12px')
        .style('pointer-events', 'none')
        .style('font-size', '14px')
        .style('color', '#222')
        .style('display', 'none')
        .style('z-index', 10);
    }

    const xLabelTooltipText = `${xAxisTitle}${xAxisUnits ? ' (' + xAxisUnits + ')' : ''}`;
    const yLabelTooltipText = `${yAxisTitle}${yAxisUnits ? ' (' + yAxisUnits + ')' : ''}`;

    // Density cloud (rendered first so it appears behind cutoff lines, dots, and
    // the regression line). Contours arrive as data-space polygon rings, so each
    // point is mapped through the x/y scales here — this keeps the cloud aligned
    // to the axes while zooming/panning.
    if (showDensityCloud && densityContours.length > 0) {
      const maxDensityValue = d3.max(densityContours, d => d.value) || 0;
      if (maxDensityValue > 0) {
        // 4-stop palette from the approved mockup, saturated so the blurred
        // cloud keeps good contrast against the plot: blue -> bright blue ->
        // green -> amber, scaled across the density value range. The nested
        // bands stack via fill-opacity to build up the dense core.
        const densityColorScale = d3.scaleLinear()
          .domain([0, maxDensityValue * 0.25, maxDensityValue * 0.5, maxDensityValue])
          .range(['#93c5fd', '#3b82f6', '#22c55e', '#eab308'])
          .interpolate(d3.interpolateRgb);

        // Fade the fill with density so the faint outer bands (near-zero mass)
        // dissolve toward the background instead of ending on a hard edge, while
        // the stacked inner bands still build the core up to nearly opaque. The
        // floor is kept fairly high (0.35, not near 0) so the cloud's extent
        // stays easy to read at a glance.
        const densityOpacityScale = d3.scaleLinear()
          .domain([0, maxDensityValue])
          .range([0.22, 0.9])
          .clamp(true);

        // Contours arrive as GeoJSON MultiPolygon geometries in data space
        // (coordinates[polygon][ring][point]). geoPath with a custom transform
        // maps each point through the chart's x/y scales, so closed rings (and
        // any holes) fill correctly and stay aligned to the axes while zooming.
        const geoPath = d3.geoPath(
          d3.geoTransform({
            point(px, py) { this.stream.point(x(px), y(py)); }
          })
        );

        plotArea.append('g')
          .attr('class', 'density-cloud')
          .attr('pointer-events', 'none')
          .attr('filter', `url(#${densityBlurId})`)
          .selectAll('path')
          .data(densityContours)
          .enter()
          .append('path')
          .attr('d', d => geoPath({ type: 'MultiPolygon', coordinates: d.coordinates || [] }))
          .attr('fill', d => densityColorScale(d.value))
          .attr('stroke', 'none')
          .attr('fill-opacity', d => densityOpacityScale(d.value));
      }
    }

    // Cutoff lines (rendered before circles so they appear behind)
    if (showCutoffLines) {
      // Determine shaded region bounds — fall back to plot edges when only one side is defined
      const xHasFloor = xAxisCutoffValues.normalFloor !== undefined && xAxisCutoffValues.normalFloor !== null;
      const xHasCeiling = xAxisCutoffValues.normalCeiling !== undefined && xAxisCutoffValues.normalCeiling !== null;
      const yHasFloor = yAxisCutoffValues.normalFloor !== undefined && yAxisCutoffValues.normalFloor !== null;
      const yHasCeiling = yAxisCutoffValues.normalCeiling !== undefined && yAxisCutoffValues.normalCeiling !== null;
      const hasAnyCutoffs = xHasFloor || xHasCeiling || yHasFloor || yHasCeiling;

      if (hasAnyCutoffs) {
        // X bounds: use cutoff if present, else clamp to plot edge
        const rectX1 = xHasFloor ? Math.max(0, x(xAxisCutoffValues.normalFloor)) : 0;
        const rectX2 = xHasCeiling ? Math.min(plotWidth, x(xAxisCutoffValues.normalCeiling)) : plotWidth;
        // Y bounds: use cutoff if present, else clamp to plot edge
        const rectY1 = yHasCeiling ? Math.max(0, y(yAxisCutoffValues.normalCeiling)) : 0;
        const rectY2 = yHasFloor ? Math.min(plotHeight, y(yAxisCutoffValues.normalFloor)) : plotHeight;

        if (rectX2 > rectX1 && rectY2 > rectY1) {
          plotArea.append('rect')
            .attr('x', rectX1)
            .attr('y', rectY1)
            .attr('width', rectX2 - rectX1)
            .attr('height', rectY2 - rectY1)
            .attr('fill', 'var(--info-color)')
            .attr('opacity', 0.06)
            .attr('pointer-events', 'none');
        }
      }

      // X-axis cutoff lines (vertical lines)
      // X-axis Normal Floor (vertical line)
      if (xHasFloor) {
        const xFloorPos = x(xAxisCutoffValues.normalFloor);
        if (xFloorPos >= 0 && xFloorPos <= plotWidth) {
          plotArea.append('line')
            .attr('x1', xFloorPos)
            .attr('x2', xFloorPos)
            .attr('y1', 0)
            .attr('y2', plotHeight)
            .attr('stroke', 'var(--info-color)')
            .attr('stroke-width', 2)
            .attr('stroke-dasharray', '5,5')
            .attr('opacity', 0.7)
            .attr('pointer-events', 'none');
          // Invisible wide hit area so the whole line (including dash gaps) is
          // easy to hover, not just the painted dashes.
          plotArea.append('line')
            .attr('class', 'cutoff-line-x-floor')
            .attr('x1', xFloorPos)
            .attr('x2', xFloorPos)
            .attr('y1', 0)
            .attr('y2', plotHeight)
            .attr('stroke', 'transparent')
            .attr('stroke-width', 16)
            .attr('pointer-events', 'stroke')
            .style('cursor', 'pointer');
        }
      }

      // X-axis Normal Ceiling (vertical line)
      if (xHasCeiling) {
        const xCeilingPos = x(xAxisCutoffValues.normalCeiling);
        if (xCeilingPos >= 0 && xCeilingPos <= plotWidth) {
          plotArea.append('line')
            .attr('x1', xCeilingPos)
            .attr('x2', xCeilingPos)
            .attr('y1', 0)
            .attr('y2', plotHeight)
            .attr('stroke', 'var(--info-color)')
            .attr('stroke-width', 2)
            .attr('stroke-dasharray', '5,5')
            .attr('opacity', 0.7)
            .attr('pointer-events', 'none');
          plotArea.append('line')
            .attr('class', 'cutoff-line-x-ceiling')
            .attr('x1', xCeilingPos)
            .attr('x2', xCeilingPos)
            .attr('y1', 0)
            .attr('y2', plotHeight)
            .attr('stroke', 'transparent')
            .attr('stroke-width', 16)
            .attr('pointer-events', 'stroke')
            .style('cursor', 'pointer');
        }
      }

      // Y-axis cutoff lines (horizontal lines)
      // Y-axis Normal Floor (horizontal line)
      if (yHasFloor) {
        const yFloorPos = y(yAxisCutoffValues.normalFloor);
        if (yFloorPos >= 0 && yFloorPos <= plotHeight) {
          plotArea.append('line')
            .attr('x1', 0)
            .attr('x2', plotWidth)
            .attr('y1', yFloorPos)
            .attr('y2', yFloorPos)
            .attr('stroke', 'var(--info-color)')
            .attr('stroke-width', 2)
            .attr('stroke-dasharray', '5,5')
            .attr('opacity', 0.7)
            .attr('pointer-events', 'none');
          plotArea.append('line')
            .attr('class', 'cutoff-line-y-floor')
            .attr('x1', 0)
            .attr('x2', plotWidth)
            .attr('y1', yFloorPos)
            .attr('y2', yFloorPos)
            .attr('stroke', 'transparent')
            .attr('stroke-width', 16)
            .attr('pointer-events', 'stroke')
            .style('cursor', 'pointer');
        }
      }

      // Y-axis Normal Ceiling (horizontal line)
      if (yHasCeiling) {
        const yCeilingPos = y(yAxisCutoffValues.normalCeiling);
        if (yCeilingPos >= 0 && yCeilingPos <= plotHeight) {
          plotArea.append('line')
            .attr('x1', 0)
            .attr('x2', plotWidth)
            .attr('y1', yCeilingPos)
            .attr('y2', yCeilingPos)
            .attr('stroke', 'var(--info-color)')
            .attr('stroke-width', 2)
            .attr('stroke-dasharray', '5,5')
            .attr('opacity', 0.7)
            .attr('pointer-events', 'none');
          plotArea.append('line')
            .attr('class', 'cutoff-line-y-ceiling')
            .attr('x1', 0)
            .attr('x2', plotWidth)
            .attr('y1', yCeilingPos)
            .attr('y2', yCeilingPos)
            .attr('stroke', 'transparent')
            .attr('stroke-width', 16)
            .attr('pointer-events', 'stroke')
            .style('cursor', 'pointer');
        }
      }
    }

    // Sort data points so highlighted dots are rendered last (on top)
    const sortedDataPoints = [...dataPoints].sort((a, b) => {
      if (a.highlight && !b.highlight) return 1; // highlighted comes after non-highlighted
      if (!a.highlight && b.highlight) return -1; // non-highlighted comes before highlighted
      return 0; // maintain original order for same type
    });

    // When the density cloud is shown, hide the regular dots so they don't
    // clutter the cloud — but always keep the current-patient (highlight) dot
    // visible so the user can still see where the patient falls.
    const visibleDataPoints = showDensityCloud && densityContours.length > 0
      ? sortedDataPoints.filter(d => d.highlight)
      : sortedDataPoints;

    plotArea.selectAll('circle')
      .data(visibleDataPoints)
      .enter()
      .append('circle')
      .attr('cx', d => x(d.x))
      .attr('cy', d => y(d.y))
      .attr('r', d => d.highlight ? 7 : 4)
      .style('fill', d => d.highlight ? accentSecondaryColor : primaryColor)
      .style('opacity', d => d.highlight ? 1 : 0.7)
      .style('stroke', d => d.highlight ? accentPrimaryColor : '#004d5a')
      .style('stroke-width', d => d.highlight ? 2 : 1);

    // Regression line (rendered AFTER circles so it appears on top)
    if (
      showRegressionLine &&
      regressionOutput &&
      typeof regressionOutput.slope === 'number' &&
      Number.isFinite(regressionOutput.slope) &&
      typeof regressionOutput.intercept === 'number' &&
      Number.isFinite(regressionOutput.intercept) &&
      typeof regressionOutput.rSquared === 'number' &&
      Number.isFinite(regressionOutput.rSquared) &&
      typeof regressionOutput.n === 'number' &&
      Number.isFinite(regressionOutput.n)
    ) {
      const { slope, intercept, rSquared, n } = regressionOutput;

      // Compute the two endpoints by intersecting the regression line with all 4 plot boundaries,
      // then keep only the segment that lies within [xMin,xMax] x [yMin,yMax].
      // Candidate intersection points:
      const candidates = [];

      // Left boundary (x = xMin): y = slope*xMin + intercept
      const yAtXMin = slope * xMin + intercept;
      if (yAtXMin >= yMin && yAtXMin <= yMax) candidates.push({ x: xMin, y: yAtXMin });

      // Right boundary (x = xMax): y = slope*xMax + intercept
      const yAtXMax = slope * xMax + intercept;
      if (yAtXMax >= yMin && yAtXMax <= yMax) candidates.push({ x: xMax, y: yAtXMax });

      // Bottom boundary (y = yMin): x = (yMin - intercept) / slope  (guard slope != 0)
      if (Math.abs(slope) > 1e-12) {
        const xAtYMin = (yMin - intercept) / slope;
        if (xAtYMin >= xMin && xAtYMin <= xMax) candidates.push({ x: xAtYMin, y: yMin });
      }

      // Top boundary (y = yMax): x = (yMax - intercept) / slope
      if (Math.abs(slope) > 1e-12) {
        const xAtYMax = (yMax - intercept) / slope;
        if (xAtYMax >= xMin && xAtYMax <= xMax) candidates.push({ x: xAtYMax, y: yMax });
      }

      // Deduplicate (corners can appear twice)
      const unique = candidates.filter((p, i, arr) =>
        arr.findIndex(q => Math.abs(q.x - p.x) < 1e-12 && Math.abs(q.y - p.y) < 1e-12) === i
      );

      if (unique.length >= 2) {
        // Sort by x so p1 is left, p2 is right
        unique.sort((a, b) => a.x - b.x);
        const p1 = unique[0];
        const p2 = unique[unique.length - 1];

        // Regression line
        plotArea.append('line')
          .attr('x1', x(p1.x))
          .attr('x2', x(p2.x))
          .attr('y1', y(p1.y))
          .attr('y2', y(p2.y))
          .attr('stroke', '#7c3aed')
          .attr('stroke-width', 3)
          .attr('opacity', 0.85)
          .attr('pointer-events', 'none');
        // Invisible wide hit area so the line is easy to hover/target.
        plotArea.append('line')
          .attr('class', 'regression-line')
          .attr('x1', x(p1.x))
          .attr('x2', x(p2.x))
          .attr('y1', y(p1.y))
          .attr('y2', y(p2.y))
          .attr('stroke', 'transparent')
          .attr('stroke-width', 18)
          .attr('pointer-events', 'stroke')
          .style('cursor', 'pointer');

        // Regression line tooltip
        g.selectAll('.regression-line')
          .on('mousemove', function(event) {
            const containerRect = container.node().getBoundingClientRect();
            const mouseX = event.clientX - containerRect.left;
            const mouseY = event.clientY - containerRect.top;
            tooltip
              .style('display', 'block')
              .html(`<strong>Regression Line</strong><br/>y = ${slope.toFixed(4)}x ${intercept >= 0 ? '+' : '-'} ${Math.abs(intercept).toFixed(4)}<br/>R² = ${rSquared.toFixed(4)}, n = ${n}`);

            const tooltipWidth = tooltip.node().offsetWidth;
            const containerWidth = containerRect.width;
            const rightEdge = mouseX + 20 + tooltipWidth;

            if (rightEdge > containerWidth) {
              tooltip.style('left', (mouseX - tooltipWidth - 10) + 'px');
            } else {
              tooltip.style('left', (mouseX + 20) + 'px');
            }
            tooltip.style('top', (mouseY - 10) + 'px');
          })
          .on('mouseout', function() {
            tooltip.style('display', 'none');
          });
      }
    }

    g.selectAll('circle')
      .on('mouseover', function(event, d) {
        tooltip
          .style('display', 'block')
          .html(
            `${d.highlight ? '<strong>Current Patient</strong><br/>' : ''}<strong>${xLabelTooltipText}:</strong> ${Number(d.x).toFixed(2)}<br/><strong>${yLabelTooltipText}:</strong> ${Number(d.y).toFixed(2)}`
          );
        if (d.highlight) {
          d3.select(this)
            .attr('stroke', accentPrimaryColor)
            .attr('stroke-width', 3)
            .style('opacity', 1)
            .attr('r', 9);
        } else {
          d3.select(this)
            .attr('stroke', accentPrimaryColor)
            .attr('stroke-width', 2)
            .style('opacity', 1)
            .attr('r', 6);
        }
      })
      .on('mousemove', function(event) {
        const containerRect = container.node().getBoundingClientRect();
        // Get mouse position relative to the container
        const mouseX = event.clientX - containerRect.left;
        const mouseY = event.clientY - containerRect.top;
        tooltip
          .style('left', (mouseX + 20) + 'px') // 20px to the right of the cursor
          .style('top', (mouseY - 50) + 'px'); // 50px above the cursor
      })
      .on('mouseout', function(event, d) {
        tooltip.style('display', 'none');
        d3.select(this)
          .style('fill', d => d.highlight ? accentSecondaryColor : primaryColor)
          .style('opacity', d => d.highlight ? 1 : 0.7)
          .style('stroke', d => d.highlight ? accentPrimaryColor : '#004d5a')
          .style('stroke-width', d => d.highlight ? 2 : 1)
          .attr('r', d => d.highlight ? 7 : 4)
      });

      // Cutoff line tooltips
      if (showCutoffLines) {
        // X-axis Normal Floor tooltip
        if (xAxisCutoffValues.normalFloor !== undefined && xAxisCutoffValues.normalFloor !== null) {
          g.selectAll('.cutoff-line-x-floor')
            .on('mousemove', function(event) {
              const containerRect = container.node().getBoundingClientRect();
              const mouseX = event.clientX - containerRect.left;
              const mouseY = event.clientY - containerRect.top;
              tooltip
                .style('display', 'block')
                .html(`<strong>${xAxisTitle} Normal Floor:</strong> ${xAxisCutoffValues.normalFloor}${xAxisUnits ? ' (' + xAxisUnits + ')' : ''}`);
              
              // Check if tooltip would overflow to the right
              const tooltipWidth = tooltip.node().offsetWidth;
              const containerWidth = containerRect.width;
              const rightEdge = mouseX + 20 + tooltipWidth;
              
              if (rightEdge > containerWidth) {
                tooltip.style('left', (mouseX - tooltipWidth - 10) + 'px');
              } else {
                tooltip.style('left', (mouseX + 20) + 'px');
              }
              tooltip.style('top', (mouseY - 10) + 'px');
            })
            .on('mouseout', function() {
              tooltip.style('display', 'none');
            });
        }

        // X-axis Normal Ceiling tooltip
        if (xAxisCutoffValues.normalCeiling !== undefined && xAxisCutoffValues.normalCeiling !== null) {
          g.selectAll('.cutoff-line-x-ceiling')
            .on('mousemove', function(event) {
              const containerRect = container.node().getBoundingClientRect();
              const mouseX = event.clientX - containerRect.left;
              const mouseY = event.clientY - containerRect.top;
              tooltip
                .style('display', 'block')
                .html(`<strong>${xAxisTitle} Normal Ceiling:</strong> ${xAxisCutoffValues.normalCeiling}${xAxisUnits ? ' (' + xAxisUnits + ')' : ''}`);
              
              // Check if tooltip would overflow to the right
              const tooltipWidth = tooltip.node().offsetWidth;
              const containerWidth = containerRect.width;
              const rightEdge = mouseX + 20 + tooltipWidth;
              
              if (rightEdge > containerWidth) {
                tooltip.style('left', (mouseX - tooltipWidth - 10) + 'px');
              } else {
                tooltip.style('left', (mouseX + 20) + 'px');
              }
              tooltip.style('top', (mouseY - 10) + 'px');
            })
            .on('mouseout', function() {
              tooltip.style('display', 'none');
            });
        }

        // Y-axis Normal Floor tooltip
        if (yAxisCutoffValues.normalFloor !== undefined && yAxisCutoffValues.normalFloor !== null) {
          g.selectAll('.cutoff-line-y-floor')
            .on('mousemove', function(event) {
              const containerRect = container.node().getBoundingClientRect();
              const mouseX = event.clientX - containerRect.left;
              const mouseY = event.clientY - containerRect.top;
              tooltip
                .style('display', 'block')
                .html(`<strong>${yAxisTitle} Normal Floor:</strong> ${yAxisCutoffValues.normalFloor}${yAxisUnits ? ' (' + yAxisUnits + ')' : ''}`);
              
              // Check if tooltip would overflow to the right
              const tooltipWidth = tooltip.node().offsetWidth;
              const containerWidth = containerRect.width;
              const rightEdge = mouseX + 20 + tooltipWidth;
              
              if (rightEdge > containerWidth) {
                tooltip.style('left', (mouseX - tooltipWidth - 10) + 'px');
              } else {
                tooltip.style('left', (mouseX + 20) + 'px');
              }
              tooltip.style('top', (mouseY - 10) + 'px');
            })
            .on('mouseout', function() {
              tooltip.style('display', 'none');
            });
        }

        // Y-axis Normal Ceiling tooltip
        if (yAxisCutoffValues.normalCeiling !== undefined && yAxisCutoffValues.normalCeiling !== null) {
          g.selectAll('.cutoff-line-y-ceiling')
            .on('mousemove', function(event) {
              const containerRect = container.node().getBoundingClientRect();
              const mouseX = event.clientX - containerRect.left;
              const mouseY = event.clientY - containerRect.top;
              tooltip
                .style('display', 'block')
                .html(`<strong>${yAxisTitle} Normal Ceiling:</strong> ${yAxisCutoffValues.normalCeiling}${yAxisUnits ? ' (' + yAxisUnits + ')' : ''}`);
              
              // Check if tooltip would overflow to the right
              const tooltipWidth = tooltip.node().offsetWidth;
              const containerWidth = containerRect.width;
              const rightEdge = mouseX + 20 + tooltipWidth;
              
              if (rightEdge > containerWidth) {
                tooltip.style('left', (mouseX - tooltipWidth - 10) + 'px');
              } else {
                tooltip.style('left', (mouseX + 20) + 'px');
              }
              tooltip.style('top', (mouseY - 10) + 'px');
            })
            .on('mouseout', function() {
              tooltip.style('display', 'none');
            });
        }
      }

  }, [dataPoints, xAxisTitle, xAxisUnits, yAxisTitle, yAxisUnits, xAxisCutoffValues, yAxisCutoffValues, showCutoffLines, showRegressionLine, regressionOutput, showDensityCloud, densityContours, width, height, xDomain, yDomain]);

  // Build a human-readable message from CRITICAL outputMessages when there is no data.
  // If multiple CRITICAL messages share a common prefix and suffix, collapse them.
  const noDataMessage = useMemo(() => {
    const criticals = (data?.outputMessages || [])
      .filter(m => m.messageLevel === 'CRITICAL')
      .map(m => m.messageContent);
    if (criticals.length === 0) return 'No Data Available';
    if (criticals.length === 1) return criticals[0];

    // Find longest common prefix (word-boundary aware)
    const findCommonPrefix = (strings) => {
      if (!strings.length) return '';
      let prefix = strings[0];
      for (let i = 1; i < strings.length; i++) {
        while (!strings[i].startsWith(prefix)) {
          prefix = prefix.slice(0, prefix.lastIndexOf(' '));
          if (!prefix) return '';
        }
      }
      return prefix;
    };

    // Find longest common suffix (word-boundary aware)
    const findCommonSuffix = (strings) => {
      if (!strings.length) return '';
      let suffix = strings[0];
      for (let i = 1; i < strings.length; i++) {
        while (!strings[i].endsWith(suffix)) {
          const idx = suffix.indexOf(' ');
          if (idx === -1) return '';
          suffix = suffix.slice(idx + 1);
        }
      }
      return suffix;
    };

    const prefix = findCommonPrefix(criticals);
    const suffix = findCommonSuffix(criticals);

    const middles = criticals.map(s => {
      let mid = s.slice(prefix.length);
      if (suffix && mid.endsWith(suffix)) mid = mid.slice(0, mid.length - suffix.length);
      return mid.trim().replace(/^[,:]\s*/, '');
    }).filter(Boolean);

    if (prefix && suffix && middles.length > 1) {
      const joined = middles.slice(0, -1).join(', ') + ' and ' + middles[middles.length - 1];
      // Reconstruct: prefix + "both X and Y" + suffix
      const both = `both ${joined}`;
      return `${prefix.trimEnd()} ${both} ${suffix.trimStart()}`;
    }

    // Fallback: join messages with a space
    return criticals.join(' ');
  }, [data?.outputMessages]);

  // Legend visibility flags (computed outside useEffect for JSX)
  const hasHighlight = dataPoints.some(d => d.highlight);
  const hasAnyCutoffData = (
    (xAxisCutoffValues.normalFloor !== undefined && xAxisCutoffValues.normalFloor !== null) ||
    (xAxisCutoffValues.normalCeiling !== undefined && xAxisCutoffValues.normalCeiling !== null) ||
    (yAxisCutoffValues.normalFloor !== undefined && yAxisCutoffValues.normalFloor !== null) ||
    (yAxisCutoffValues.normalCeiling !== undefined && yAxisCutoffValues.normalCeiling !== null)
  );
  const showRegressionLegend = showRegressionLine && regressionOutput &&
    typeof regressionOutput.slope === 'number' && Number.isFinite(regressionOutput.slope) &&
    typeof regressionOutput.intercept === 'number' && Number.isFinite(regressionOutput.intercept);
  const showCutoffLegend = showCutoffLines && hasAnyCutoffData;
  const showDensityLegend = showDensityCloud && densityContours.length > 0;
  const showLegend = !showNoData && (showRegressionLegend || showCutoffLegend || showDensityLegend || hasHighlight);

  return (
  <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
    {showNoData ? (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: `${height}px`,
        fontSize: '18px',
        color: '#666',
        fontFamily: '"Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
        textAlign: 'center',
        padding: '0 24px',
      }}>
        {noDataMessage}
      </div>
    ) : (
      <>
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          {/* Vertical (Y-axis) zoom slider, aligned to the plot area */}
          <Box
            sx={{
              width: `${Y_SLIDER_COL}px`,
              height: `${height}px`,
              pt: `${CHART_MARGIN.top}px`,
              pb: `${CHART_MARGIN.bottom}px`,
              display: 'flex',
              justifyContent: 'center',
              boxSizing: 'border-box',
              flexShrink: 0,
            }}
          >
            <Slider
              orientation="vertical"
              min={defaultDomain.y[0]}
              max={defaultDomain.y[1]}
              step={ySliderStep}
              value={clampDomain(yDomain, defaultDomain.y)}
              onChange={handleYSliderChange}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => Number(v).toFixed(2)}
              getAriaLabel={(index) => (index === 0 ? 'Y axis zoom minimum' : 'Y axis zoom maximum')}
              ref={ySliderRef}
            />
          </Box>
          {/* Chart column */}
          <div style={{ width: `${width}px`, position: 'relative', flexShrink: 0 }}>
            <svg
              ref={svgRef}
              width={width}
              height={height}
              viewBox={`0 0 ${width} ${height}`}
              preserveAspectRatio="xMinYMin meet"
              xmlns="http://www.w3.org/2000/svg"
            />
          </div>
        </div>
        {/* Horizontal (X-axis) zoom slider, aligned to the plot width */}
        <Box
          sx={{
            pl: `${Y_SLIDER_COL + CHART_MARGIN.left}px`,
            pr: `${CHART_MARGIN.right}px`,
            boxSizing: 'border-box',
          }}
        >
          <Slider
            min={defaultDomain.x[0]}
            max={defaultDomain.x[1]}
            step={xSliderStep}
            value={clampDomain(xDomain, defaultDomain.x)}
            onChange={handleXSliderChange}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => Number(v).toFixed(2)}
            getAriaLabel={(index) => (index === 0 ? 'X axis zoom minimum' : 'X axis zoom maximum')}
          />
        </Box>
        {/* Reset control + legend */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          padding: '4px 0',
        }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RestartAlt />}
            onClick={handleResetZoom}
            aria-label="Reset zoom to default view"
          >
            Reset Zoom
          </Button>
          {showLegend ? (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              gap: '16px',
              fontSize: '13px',
              color: '#555',
              fontFamily: '"Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
            }}>
              {showDensityLegend && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="24" height="12">
                    <defs>
                      <linearGradient id="density-legend-gradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#93c5fd" />
                        <stop offset="25%" stopColor="#3b82f6" />
                        <stop offset="50%" stopColor="#22c55e" />
                        <stop offset="100%" stopColor="#eab308" />
                      </linearGradient>
                    </defs>
                    <rect x="0" y="1" width="24" height="10" rx="2" fill="url(#density-legend-gradient)" opacity="0.8" />
                  </svg>
                  <span>Density Cloud</span>
                </div>
              )}
              {showRegressionLegend && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="24" height="12">
                    <line x1="0" y1="6" x2="24" y2="6" stroke="#7c3aed" strokeWidth="3" opacity="0.85" />
                  </svg>
                  <span>Regression Line</span>
                </div>
              )}
              {showCutoffLegend && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="24" height="12">
                    <line x1="0" y1="6" x2="24" y2="6" stroke={infoColor || '#4173B4'} strokeWidth="2" strokeDasharray="5,5" opacity="0.7" />
                  </svg>
                  <span>Cutoff Lines</span>
                </div>
              )}
              {hasHighlight && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="16" height="16">
                    <circle cx="8" cy="8" r="6" fill={accentSecondaryColor || '#ffad42'} stroke={accentPrimaryColor || '#bb4d00'} strokeWidth="2" opacity="0.85" />
                  </svg>
                  <span>Current Patient</span>
                </div>
              )}
            </div>
          ) : <span />}
        </div>
      </>
    )}
    </div>
  );
});

export default ScatterplotChart;